import { isVectorSource, type SourceDescriptor, type SpatialExtent } from '../data/model';
import type { LayerDefinition } from '../project/model';

function shiftedExtent(extent: SpatialExtent, shift: number): SpatialExtent {
  return {
    ...extent,
    west: extent.west + shift,
    east: extent.east + shift,
  };
}

function mergeLongitudes(current: SpatialExtent, next: SpatialExtent): SpatialExtent {
  const candidates = [-360, 0, 360].map((shift) => shiftedExtent(next, shift));
  const best = candidates.reduce((narrowest, candidate) => {
    const candidateWidth =
      Math.max(current.east, candidate.east) - Math.min(current.west, candidate.west);
    const narrowestWidth =
      Math.max(current.east, narrowest.east) - Math.min(current.west, narrowest.west);
    return candidateWidth < narrowestWidth ? candidate : narrowest;
  });

  const west = Math.min(current.west, best.west);
  const east = Math.max(current.east, best.east);
  return {
    west,
    south: Math.min(current.south, best.south),
    east,
    north: Math.max(current.north, best.north),
    crossesAntimeridian:
      current.crossesAntimeridian || next.crossesAntimeridian || west < -180 || east > 180,
  };
}

export function extentForLayers(
  sources: readonly SourceDescriptor[],
  layers: readonly LayerDefinition[],
): SpatialExtent | null {
  const referencedSourceIds = new Set(layers.map((layer) => layer.sourceId));
  const extents = sources
    .filter(isVectorSource)
    .filter((source) => referencedSourceIds.has(source.id))
    .map((source) => source.extent)
    .filter((extent): extent is SpatialExtent => extent !== null);

  const first = extents[0];
  return first ? extents.slice(1).reduce(mergeLongitudes, first) : null;
}
